import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product } from '../types';
import { PRODUCTS } from '../src/data/products';

// Generate serial numbers once at module scope to avoid impure function calls during render
const SERIAL_NUMBERS = PRODUCTS.map(() => Math.floor(Math.random() * 9000) + 1000);

const ProductCard: React.FC<{ product: Product; serialNumber: number }> = ({
  product,
  serialNumber,
}) => {
  const { t } = useTranslation();
  return (
    <div className="border-[3px] border-black bg-white flex flex-col shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
      <div className="p-3 border-b-[3px] border-black bg-gray-100 flex justify-between items-center">
        <div className="text-[10px] font-black uppercase">
          {t('products.documentId')} {product.id}
        </div>
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-black"></div>
          <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
        </div>
      </div>

      <Link to={`/product/${product.slug}`} className="block">
        <div className="aspect-[4/5] overflow-hidden border-b-[3px] border-black grainy-overlay bg-gray-200">
          <img
            src={product.imageUrl}
            alt={product.imageAlt || `${product.name} - Yugoslav ${product.classification === 'tshirt' ? 'T-Shirt' : product.classification === 'hoodie' ? 'Hoodie' : product.classification === 'poster' ? 'Poster' : product.classification === 'totebag' ? 'Tote Bag' : product.classification} featuring brutalist design`}
            className="w-full h-full object-cover grayscale contrast-125 hover:grayscale-0 transition-all duration-500"
            loading="lazy"
          />
        </div>
      </Link>

      <div className="p-4 flex-1 flex flex-col">
        <div className="mb-4">
          <label className="text-[10px] font-black text-gray-400 block mb-1 font-typewriter">
            {t('products.docNo')}
          </label>
          <div className="text-lg font-black tracking-tight uppercase">{product.docNo}</div>
        </div>

        <div className="mb-4">
          <label className="text-[10px] font-black text-gray-400 block mb-1 font-typewriter">
            {t('products.classification')}
          </label>
          <div className="text-xs font-bold leading-tight h-10 overflow-hidden uppercase">
            {t(`products.items.${product.classification}`)}
          </div>
        </div>

        <div className="mb-6 flex justify-between items-end">
          <div>
            <label className="text-[10px] font-black text-gray-400 block mb-1 font-typewriter">
              {t('products.issuanceCost')}
            </label>
            <div className="text-xl font-black text-[#C41E3A]">
              {product.price ? `$${product.price.toFixed(2)}` : t(product.cost)}
            </div>
          </div>
          <div className="text-[8px] font-typewriter text-gray-400 text-right uppercase">
            {t('products.serialPrefix')}
            <br />
            BC-{serialNumber}
          </div>
        </div>

        <Link
          to={`/product/${product.slug}`}
          className="mt-auto block text-center bg-[#C41E3A] border-2 border-transparent hover:border-black text-white py-3 font-black uppercase tracking-tighter hover:bg-black transition-all active:translate-y-1"
        >
          {t('products.viewDetails')}
        </Link>
      </div>

      <div className="h-6 border-t-[3px] border-black flex overflow-hidden">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className="flex-none w-2 border-r border-black last:border-r-0 bg-gray-50 odd:bg-gray-200"
          ></div>
        ))}
      </div>
    </div>
  );
};

const ProductGrid: React.FC = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-b-[3px] border-black">
      {PRODUCTS.map((product, index) => (
        <div
          key={product.id}
          className="border-r-[3px] border-black last:border-r-0 p-6 md:p-8 bg-[#F0F0F0]"
        >
          <ProductCard product={product} serialNumber={SERIAL_NUMBERS[index]} />
        </div>
      ))}
    </div>
  );
};

export default ProductGrid;
